#define GLSLIFY 1
uniform sampler2D uMap;
uniform sampler2D uBlendMap;
uniform float uElapsed;
uniform float uEdge;
uniform float uEdgeAlpha;
uniform float uGrain;
uniform float uFog;
uniform float uBlend;
uniform vec3 uFogColor;

varying float vDistance;
varying float vIdOffset;
varying vec2 vUv;
varying vec3 vObjectNormal;
varying vec2 vUvOffset;
varying vec2 vNextUvOffset;

const float PI = 3.141592653589793;
const float lodLevel = 4.0;

float random(vec2 p) {
  vec2 K1 = vec2(
    23.14069263277926, // e^pi (Gelfond's constant)
    2.665144142690225 // 2^sqrt(2) (Gelfond–Schneider constant)
  );
  return fract(cos(dot(p, K1)) * 12345.6789);
}

vec2 rotateUV(vec2 uv, float rotation, vec2 center) {
  float s = sin(rotation);
  float c = cos(rotation);
  uv -= center;
  mat2 rot = mat2(c, -s, s, c);
  uv = rot * uv;
  uv += center;
  return uv;
}

vec2 setUvOffset(vec2 uv, vec2 offset) {
  uv *= vec2(0.5);
  uv += offset;
  return uv;
}

vec3 getColor(float dotX, float dotY, float dotZ, sampler2D map, vec2 uv, vec2 uvOffset) {
  vec3 color;

  if (abs(dotX) > 0.5) {
    color = texture(map, setUvOffset(vUv, uvOffset)).rgb;
  } else if (dotY > 0.5) {
    vec2 uv = rotateUV(vUv, PI, vec2(0.5));
    vec3 c1 = textureLod(map, setUvOffset(vec2(1.0 - uv.x, 0.99), uvOffset), lodLevel).rgb;
    vec3 c2 = textureLod(map, setUvOffset(vec2(uv.x, 0.99), uvOffset), lodLevel).rgb;
    color = mix(c2, c1, uv.y);
  } else if (dotY < -0.5) {
    vec2 uv = rotateUV(vUv, PI, vec2(0.5));
    vec3 c1 = textureLod(map, setUvOffset(vec2(uv.x, 0.01), uvOffset), lodLevel).rgb;
    vec3 c2 = textureLod(map, setUvOffset(vec2(1.0 - uv.x, 0.01), uvOffset), lodLevel).rgb;
    color = mix(c2, c1, uv.y);
  } else {
    vec2 uv = vUv;
    vec3 c1 = textureLod(map, setUvOffset(vec2(0.01, uv.y), uvOffset), lodLevel).rgb;
    vec3 c2 = textureLod(map, setUvOffset(vec2(0.99, uv.y), uvOffset), lodLevel).rgb;
    color = mix(c2, c1, uv.x);
  }

  return color;
}

void main() {
  float dotX = dot(vObjectNormal, normalize(vec3(1.0, 0.0, 0.0)));
  float dotY = dot(vObjectNormal, normalize(vec3(0.0, 1.0, 0.0)));
  float dotZ = dot(vObjectNormal, normalize(vec3(0.0, 0.0, 1.0)));

  vec3 color = getColor(dotX, dotY, dotZ, uMap, vUv, vUvOffset);
  vec3 nextColor = getColor(dotX, dotY, dotZ, uMap, vUv, vNextUvOffset);

  if (uBlend > 0.0) {
    color = mix(color, getColor(dotX, dotY, dotZ, uBlendMap, vUv, vUvOffset), uBlend);
    nextColor = mix(nextColor, getColor(dotX, dotY, dotZ, uBlendMap, vUv, vNextUvOffset), uBlend);
  }

  float left = smoothstep(uEdge, 0.0, vUv.x);
  float right = smoothstep(uEdge, 0.0, 1.0 - vUv.x);
  float top = smoothstep(uEdge, 0.0, vUv.y);
  float bottom = smoothstep(uEdge, 0.0, 1.0 - vUv.y);
  float edge = max(max(left, right), max(top, bottom));

  color = mix(color, nextColor, vIdOffset);

  vec2 uvRandom = vUv;
  uvRandom.y *= random(vec2(vUv.y));
  color.rgb += random(uvRandom) * uGrain;
  color.rgb += vec3(0.05) * edge * 2.0;

  float fog = smoothstep(10.0, 11.0, vDistance);
  color = mix(color, uFogColor, fog * uFog);

  if (uElapsed < 1.0) {
    float dist = mix(-1.0, 15.0, uElapsed);
    float anim = smoothstep(dist, dist + 6.0, vDistance);
    color = mix(color, uFogColor, anim);
  }

  gl_FragColor = vec4(color, 1.0 - edge * uEdgeAlpha);
}
